import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardCheck,
  Users,
  Loader2,
  Plus,
  Trash2,
  Home,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../utils/cn';
import { sanitizePersonName } from '../../utils/fullName';
import {
  commonAreaReservationService,
  CommonAreaBooking,
  CommonAreaBookingGuest,
  CommonAreaChecklistItem,
  CommonAreaChecklistResponse,
  GuestMode,
  GUEST_MODE_LABELS,
  ImportableVisitor,
  effectiveGuestModes,
} from '../../services/commonAreaReservationService';

/**
 * Check-in + guest list for one reservation.
 *
 * One component for both surfaces (operational view and reservations page) and both audiences,
 * selected by `variant`:
 *   staff    → area-scoped routes, role-gated by the backend
 *   resident → booking-scoped routes, authorized by ownership
 *
 * The variant is not cosmetic: the two pairs of endpoints have different authorization, so calling
 * the wrong one either 403s the resident or bypasses the ownership check. That is why the tests
 * assert which service method was called rather than just "it saved".
 */

/** The subset of the area this panel needs — callers pass whatever shape they already hold. */
export interface CheckinPanelArea {
  id: string;
  name?: string;
  capacity?: number;
  checkin_checklist_enabled?: boolean;
  checklist_responsible_can_complete?: boolean;
  guest_list_modes?: GuestMode[];
  guest_list_required?: boolean;
}

export interface BookingCheckinPanelProps {
  area: CheckinPanelArea;
  booking: Pick<CommonAreaBooking, 'id' | 'status' | 'unit_label' | 'checked_in_at' | 'checked_in_by_name'>;
  variant: 'staff' | 'resident';
  /** Called after a successful check-in so the parent list can reflect the new state. */
  onUpdated?: (patch: Partial<CommonAreaBooking>) => void;
  darkMode?: boolean;
}

const ACTIVE_STATUSES = ['Pending', 'Confirmed', 'Approved'];

export const BookingCheckinPanel: React.FC<BookingCheckinPanelProps> = ({
  area,
  booking,
  variant,
  onUpdated,
  darkMode = false,
}) => {
  const isStaff = variant === 'staff';
  const allowedModes = useMemo(() => effectiveGuestModes(area), [area]);

  const [items, setItems] = useState<CommonAreaChecklistItem[]>([]);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [responses, setResponses] = useState<CommonAreaChecklistResponse[]>([]);
  const [guests, setGuests] = useState<CommonAreaBookingGuest[]>([]);
  const [visitors, setVisitors] = useState<ImportableVisitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [adding, setAdding] = useState(false);
  const [checkedInAt, setCheckedInAt] = useState<string | undefined>(booking.checked_in_at);
  const [checkedInBy, setCheckedInBy] = useState<string | undefined>(booking.checked_in_by_name);

  // New-guest draft
  const [guestMode, setGuestMode] = useState<GuestMode>(allowedModes[0]);
  const [guestName, setGuestName] = useState('');
  const [guestDocument, setGuestDocument] = useState('');
  const [guestPersonId, setGuestPersonId] = useState('');

  const isActive = ACTIVE_STATUSES.includes(booking.status);
  const checklistOn = !!area.checkin_checklist_enabled;
  const canCheckIn = isActive && (isStaff || !!area.checklist_responsible_can_complete);
  const atCapacity = !!area.capacity && area.capacity > 0 && guests.length >= area.capacity;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedGuests, loadedItems, loadedResponses] = await Promise.all([
        isStaff
          ? commonAreaReservationService.getGuests(area.id, booking.id)
          : commonAreaReservationService.getMyBookingGuests(booking.id),
        checklistOn ? commonAreaReservationService.getChecklistItems(area.id) : Promise.resolve([]),
        // Responses are a staff-only read; a resident sees the outcome on the booking itself.
        checklistOn && isStaff
          ? commonAreaReservationService.getChecklistResponses(area.id, booking.id)
          : Promise.resolve([]),
      ]);
      setGuests(loadedGuests);
      setItems(loadedItems.filter(i => i.is_active));
      setResponses(loadedResponses);
      // Pre-tick from what was already answered, so re-opening a completed check-in shows it
      // rather than an empty form that looks un-done.
      const prefilled: Record<string, boolean> = {};
      const prefilledNotes: Record<string, string> = {};
      loadedResponses.forEach(r => {
        if (r.checklist_item_id) {
          prefilled[r.checklist_item_id] = r.checked;
          if (r.notes) prefilledNotes[r.checklist_item_id] = r.notes;
        }
      });
      setAnswers(prefilled);
      setNotes(prefilledNotes);
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao carregar a reserva');
    } finally {
      setLoading(false);
    }
  }, [area.id, booking.id, checklistOn, isStaff]);

  useEffect(() => {
    void load();
  }, [load]);

  // Importable visitors are only fetched when the mode is actually offered — it is a staff-only
  // route and an extra request on every panel otherwise.
  useEffect(() => {
    if (!isStaff || !allowedModes.includes('import_visitors')) return;
    commonAreaReservationService
      .getImportableVisitors(area.id, booking.id)
      .then(setVisitors)
      .catch(() => setVisitors([]));
  }, [isStaff, allowedModes, area.id, booking.id]);

  const handleCheckin = async () => {
    const payload = items.map(item => ({
      item_id: item.id,
      checked: !!answers[item.id],
      notes: notes[item.id] || '',
    }));

    setCheckingIn(true);
    try {
      const result = isStaff
        ? await commonAreaReservationService.completeCheckin(area.id, booking.id, payload)
        : await commonAreaReservationService.completeCheckinAsResponsible(booking.id, payload);
      setCheckedInAt(result.checked_in_at);
      setCheckedInBy(result.checked_in_by_name);
      setResponses(result.responses || []);
      toast.success('Check-in registrado');
      onUpdated?.({ checked_in_at: result.checked_in_at, checked_in_by_name: result.checked_in_by_name });
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao registrar o check-in');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleAddGuest = async () => {
    const isImport = guestMode === 'import_visitors';
    // Guarded here as well as disabled in the UI: the button is also reachable by keyboard while
    // the field is empty.
    if (isImport ? !guestPersonId : !guestName.trim()) return;

    setAdding(true);
    try {
      const input = isImport
        ? { mode: guestMode, person_id: guestPersonId }
        : { mode: guestMode, name: guestName.trim(), document: guestDocument.trim() || undefined };
      const guest = isStaff
        ? await commonAreaReservationService.addGuestAsStaff(area.id, booking.id, input)
        : await commonAreaReservationService.addMyBookingGuest(booking.id, input);
      setGuests(prev => [...prev, guest]);
      setGuestName('');
      setGuestDocument('');
      setGuestPersonId('');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao adicionar convidado');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveGuest = async (guest: CommonAreaBookingGuest) => {
    try {
      if (isStaff) {
        await commonAreaReservationService.deleteGuestAsStaff(area.id, booking.id, guest.id);
      } else {
        await commonAreaReservationService.deleteMyBookingGuest(booking.id, guest.id);
      }
      setGuests(prev => prev.filter(g => g.id !== guest.id));
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao remover convidado');
    }
  };

  const text = darkMode ? 'text-gray-200' : 'text-gray-900';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const border = darkMode ? 'border-gray-800' : 'border-gray-200';
  const surface = darkMode ? 'bg-gray-900/50' : 'bg-gray-50';
  const inputCls = cn(
    'border rounded-md py-1.5 px-2 text-sm focus:outline-none focus:ring-2',
    darkMode ? 'bg-gray-900 border-gray-700 text-gray-200 focus:ring-gray-600' : 'border-gray-300 focus:ring-black',
  );

  const requiredMissing = checklistOn && items.some(i => i.is_required && !answers[i.id]);

  return (
    <div className="space-y-4">
      {booking.unit_label && (
        <div className={cn('flex items-center gap-1.5 text-sm', muted)}>
          <Home className="h-3.5 w-3.5" />
          Unidade <span className={cn('font-medium', text)}>{booking.unit_label}</span>
        </div>
      )}

      {loading ? (
        <div className={cn('flex items-center gap-2 py-3 text-sm', muted)}>
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando...
        </div>
      ) : (
        <>
          {/* ── Check-in ── */}
          {checklistOn && (
            <div className={cn('rounded-lg border p-3 space-y-2', border, surface)}>
              <div className="flex items-center gap-1.5">
                <ClipboardCheck className={cn('h-4 w-4', muted)} />
                <span className={cn('text-sm font-medium', text)}>Checklist de check-in</span>
              </div>

              {items.length === 0 ? (
                <p className={cn('text-xs italic', muted)}>Nenhum item configurado para este local.</p>
              ) : (
                <div className="space-y-1.5">
                  {items.map(item => (
                    <label key={item.id} className="flex items-start gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={!!answers[item.id]}
                        onChange={e => setAnswers(prev => ({ ...prev, [item.id]: e.target.checked }))}
                        disabled={!canCheckIn}
                        className="mt-0.5 accent-emerald-600"
                        aria-label={item.label}
                      />
                      <span className={cn('text-sm', text)}>
                        {item.label}
                        {item.is_required && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-500">obrigatório</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {requiredMissing && canCheckIn && (
                <p className="text-xs text-gray-600 flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  Há itens obrigatórios não marcados — o check-in será recusado.
                </p>
              )}
            </div>
          )}

          {checkedInAt ? (
            <p className="text-sm text-emerald-600 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              Check-in em {new Date(checkedInAt).toLocaleString('pt-BR')}
              {checkedInBy ? ` por ${checkedInBy}` : ''}
            </p>
          ) : null}

          {canCheckIn ? (
            <button
              type="button"
              onClick={handleCheckin}
              disabled={checkingIn}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
              {checkedInAt ? 'Atualizar check-in' : 'Confirmar check-in'}
            </button>
          ) : (
            isActive && (
              <p className={cn('text-xs', muted)}>
                O check-in deste local é feito pela portaria ou pela administração.
              </p>
            )
          )}

          {/* ── Guests ── */}
          <div className={cn('rounded-lg border p-3 space-y-3', border, surface)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Users className={cn('h-4 w-4', muted)} />
                <span className={cn('text-sm font-medium', text)}>Convidados</span>
              </div>
              <span className={cn('text-xs', muted)}>
                {guests.length}
                {area.capacity ? ` / ${area.capacity}` : ''}
              </span>
            </div>

            {area.guest_list_required && guests.length === 0 && (
              <p className="text-xs text-gray-600 flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                Este local exige a lista de convidados antes do check-in.
              </p>
            )}

            {guests.length > 0 && (
              <ul className="space-y-1">
                {guests.map(guest => (
                  <li
                    key={guest.id}
                    className={cn(
                      'flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm',
                      darkMode ? 'bg-gray-900' : 'bg-white',
                    )}
                  >
                    <span className={cn('min-w-0 truncate', text)}>
                      {guest.name}
                      {guest.document && <span className={cn('ml-2 text-xs', muted)}>{guest.document}</span>}
                      {guest.mode && guest.mode !== 'name_only' && (
                        <span className={cn('ml-2 text-[10px] uppercase tracking-wide', muted)}>
                          {GUEST_MODE_LABELS[guest.mode]}
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveGuest(guest)}
                      aria-label={`Remover ${guest.name}`}
                      className={cn('p-1 rounded hover:text-red-600 flex-shrink-0', muted)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {atCapacity ? (
              <p className={cn('text-xs', muted)}>Capacidade máxima de convidados atingida.</p>
            ) : null}

            <div className="flex flex-wrap items-end gap-2">
              {allowedModes.length > 1 && (
                <div>
                  <label htmlFor="guest-mode" className={cn('block text-[11px] mb-1', muted)}>
                    Como adicionar
                  </label>
                  <select
                    id="guest-mode"
                    value={guestMode}
                    onChange={e => setGuestMode(e.target.value as GuestMode)}
                    className={inputCls}
                  >
                    {allowedModes.map(mode => (
                      <option key={mode} value={mode}>
                        {GUEST_MODE_LABELS[mode]}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {guestMode === 'import_visitors' ? (
                <div>
                  <label htmlFor="guest-visitor" className={cn('block text-[11px] mb-1', muted)}>
                    Visitante
                  </label>
                  <select
                    id="guest-visitor"
                    value={guestPersonId}
                    onChange={e => setGuestPersonId(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Selecione...</option>
                    {visitors.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.first_name} {v.last_name}
                        {v.cpf ? ` — ${v.cpf}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="guest-name" className={cn('block text-[11px] mb-1', muted)}>
                      Nome do convidado
                    </label>
                    <input
                      id="guest-name"
                      type="text"
                      value={guestName}
                      onChange={e => setGuestName(sanitizePersonName(e.target.value))}
                      placeholder="Nome completo"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="guest-doc" className={cn('block text-[11px] mb-1', muted)}>
                      Documento
                    </label>
                    <input
                      id="guest-doc"
                      type="text"
                      value={guestDocument}
                      onChange={e => setGuestDocument(e.target.value)}
                      placeholder="Opcional"
                      className={cn(inputCls, 'w-28')}
                    />
                  </div>
                </>
              )}

              <button
                type="button"
                onClick={handleAddGuest}
                disabled={adding || atCapacity}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg disabled:opacity-50',
                  darkMode ? 'bg-gray-800 text-gray-100 hover:bg-gray-700' : 'bg-gray-900 text-white hover:bg-gray-700',
                )}
              >
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Adicionar convidado
              </button>
            </div>

            {responses.length > 0 && !checklistOn && null}
          </div>
        </>
      )}
    </div>
  );
};
